/**
 * PUBLIC party-invite claim page — usebero.com/i/<code>. No login. A guest
 * opens the host's link, enters their name, and gets a personal gate pass
 * (lands on their own /p/<code> ticket). States: open / full / expired /
 * cancelled / invalid.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Text as RNText, TextInput, View } from 'react-native';

import { palette, radius, space } from '@bero/tokens';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { AppText, Screen } from '@/ui';

const BRAND = palette.light;
const FIG = palette.dark.accent500;

type Invite = {
  title: string; state: 'open' | 'full' | 'expired' | 'cancelled';
  remaining: number; validFrom?: string; validUntil?: string;
  society: string; hostName: string; flatCode: string;
};

function Mark({ height = 20 }: { height?: number }) {
  const u = height / 58;
  return (
    <View style={{ width: 50 * u, height }}>
      <View style={{ position: 'absolute', left: 2 * u, top: 1 * u, width: 46 * u, height: 55 * u, borderWidth: 9 * u, borderBottomWidth: 0, borderColor: BRAND.canvas0, borderTopLeftRadius: 23 * u, borderTopRightRadius: 23 * u }} />
      <View style={{ position: 'absolute', left: 19 * u, top: 24 * u, width: 12 * u, height: 12 * u, borderRadius: 6 * u, backgroundColor: FIG }} />
      <View style={{ position: 'absolute', left: 20 * u, top: 40 * u, width: 10 * u, height: 16 * u, borderRadius: 5 * u, backgroundColor: FIG }} />
    </View>
  );
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtWindow(fromIso?: string, untilIso?: string): string {
  const f = fromIso ? new Date(fromIso) : null;
  const u = untilIso ? new Date(untilIso) : null;
  const clock = (d: Date) => {
    let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
    return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
  };
  if (f && u && !Number.isNaN(f.getTime()) && !Number.isNaN(u.getTime())) {
    return `${f.getDate()} ${MON[f.getMonth()]} · ${clock(f)} – ${clock(u)}`;
  }
  return '';
}

// Reactive: resolve the label with the hook t() at render, never here.
const CLOSED_KEYS: Record<string, DictKey> = {
  full: 'pi2_closed_full',
  expired: 'pi2_closed_expired',
  cancelled: 'pi2_closed_cancelled',
};

export default function PublicInvite() {
  const { code } = useLocalSearchParams<{ code: string }>();
  const { t } = useT();
  const [invite, setInvite] = React.useState<Invite | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'notfound'>('loading');
  const [name, setName] = React.useState('');
  const [claiming, setClaiming] = React.useState(false);
  const [claimError, setClaimError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!code) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/visitors/invite/${code}`);
        if (!res.ok) { if (!cancelled) setState('notfound'); return; }
        const data = (await res.json()) as Invite;
        if (!cancelled) { setInvite(data); setState('ready'); }
      } catch {
        if (!cancelled) setState('notfound');
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const claim = async () => {
    if (!name.trim() || claiming) return;
    setClaiming(true);
    setClaimError(null);
    try {
      const res = await fetch(`/api/visitors/invite/${code}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = (await res.json()) as { passCode?: string; error?: string };
      if (!res.ok || !data.passCode) {
        setClaimError(data.error ?? t('pi2_err_generic'));
        setClaiming(false);
        return;
      }
      router.replace(`/p/${data.passCode}`);
    } catch {
      setClaimError(t('pi2_err_network'));
      setClaiming(false);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: BRAND.canvas1 }}>
      <Screen padded safeTop style={{ paddingTop: space.s8, alignItems: 'center' }}>
        {state === 'loading' ? (
          <AppText tone="tertiary" style={{ marginTop: space.s10, color: BRAND.textTertiary }}>{t('pi2_loading')}</AppText>
        ) : state === 'notfound' || !invite ? (
          <View style={{ alignItems: 'center', marginTop: space.s10, gap: space.s3 }}>
            <Mark height={40} />
            <AppText variant="headingSm" weight="600" style={{ color: BRAND.textPrimary }}>{t('pi2_invalid_title')}</AppText>
            <AppText variant="bodyMd" style={{ textAlign: 'center', maxWidth: 300, color: BRAND.textSecondary }}>
              {t('pi2_invalid_body')}
            </AppText>
          </View>
        ) : (
          <View style={{
            width: '100%', maxWidth: 360, alignSelf: 'center',
            backgroundColor: BRAND.canvas0, borderRadius: radius.xxl, overflow: 'hidden',
            borderWidth: 1, borderColor: BRAND.borderSoft,
          }}>
            <View style={{ backgroundColor: BRAND.primary700, paddingVertical: space.s4, paddingHorizontal: space.s5, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Mark height={20} />
              <RNText style={{ fontFamily: 'Sora_800ExtraBold', fontSize: 16, letterSpacing: -0.64, color: BRAND.canvas0 }}>bero</RNText>
              <AppText variant="label" style={{ marginLeft: 'auto', color: BRAND.primary100 }} numberOfLines={1}>
                {t('pi2_society_invite', { society: invite.society })}
              </AppText>
            </View>

            <View style={{ padding: space.s5 }}>
              <AppText variant="headingMd" weight="600" style={{ textAlign: 'center', color: BRAND.textPrimary }}>
                {invite.title}
              </AppText>
              <AppText variant="bodyMd" style={{ textAlign: 'center', marginTop: 4, color: BRAND.textSecondary }}>
                {invite.hostName} · {invite.flatCode}
              </AppText>
              {fmtWindow(invite.validFrom, invite.validUntil) ? (
                <AppText variant="bodySm" style={{ textAlign: 'center', marginTop: 6, color: BRAND.textTertiary }}>
                  {fmtWindow(invite.validFrom, invite.validUntil)}
                </AppText>
              ) : null}

              {invite.state === 'open' ? (
                <>
                  <AppText variant="bodySm" style={{ textAlign: 'center', marginTop: space.s4, color: BRAND.textSecondary }}>
                    {t('pi2_claim_prompt', {
                      extra: invite.remaining <= 5 ? t('pi2_n_left', { n: invite.remaining }) : '',
                    })}
                  </AppText>
                  <TextInput
                    value={name}
                    onChangeText={setName}
                    placeholder={t('pi2_name_ph')}
                    placeholderTextColor={BRAND.textTertiary}
                    style={{
                      marginTop: space.s3, minHeight: 48, borderRadius: 10, borderWidth: 1,
                      borderColor: BRAND.border, paddingHorizontal: space.s4, fontSize: 16,
                      color: BRAND.textPrimary, backgroundColor: BRAND.canvas0,
                    }}
                  />
                  {claimError ? (
                    <AppText variant="bodySm" style={{ marginTop: space.s2, color: BRAND.danger700 }}>{claimError}</AppText>
                  ) : null}
                  <RNText
                    accessibilityRole="button"
                    onPress={() => void claim()}
                    style={{
                      marginTop: space.s4, overflow: 'hidden', textAlign: 'center',
                      backgroundColor: name.trim() && !claiming ? BRAND.primary500 : BRAND.canvas2,
                      color: name.trim() && !claiming ? BRAND.canvas0 : BRAND.textTertiary,
                      paddingVertical: 14, borderRadius: 12, fontSize: 16, fontWeight: '600',
                    }}
                  >
                    {claiming ? t('pi2_getting') : t('pi2_get_pass')}
                  </RNText>
                </>
              ) : (
                <View style={{ marginTop: space.s4, backgroundColor: BRAND.canvas1, borderRadius: 12, padding: space.s4 }}>
                  <AppText variant="bodyMd" style={{ textAlign: 'center', color: BRAND.textSecondary }}>
                    {t(CLOSED_KEYS[invite.state])}
                  </AppText>
                </View>
              )}
            </View>
          </View>
        )}
        {state === 'ready' && invite?.state === 'open' ? (
          <AppText variant="bodySm" style={{ marginTop: space.s5, textAlign: 'center', maxWidth: 340, color: BRAND.textTertiary }}>
            {t('pi2_footer')}
          </AppText>
        ) : null}
      </Screen>
    </View>
  );
}
